/**
 * The ledger as SQL: the source every query reads, and the small pieces the
 * stores build their questions from. Pure — nothing here runs a query.
 *
 * Every question the screen asks of the transactions — the list, its totals,
 * the balance, every chart — is answered by the database rather than by
 * JavaScript walking a copy of every row. The rows never all come over: the
 * list is read a page at a time, and everything else arrives already added up.
 *
 * `LEDGER` is what those questions are asked of. It is the real transactions
 * with their category's name joined in, plus the goals' projected savings
 * (`lib/savings.js` works those out; they live in no table) passed in as one
 * JSON parameter, in the same shape. `saving` tells the two apart. `worn` is
 * every tag every transaction wears — its own, and the ones its category hands
 * down, which are written nowhere.
 *
 * A statement built on it starts `${LEDGER} SELECT …` and its first parameter
 * is `savingsParam(rows)`.
 */

export const LEDGER = `WITH
  savings AS (
    SELECT json_extract(value, '$.id') AS id,
           json_extract(value, '$.date') AS date,
           json_extract(value, '$.amount') AS amount,
           json_extract(value, '$.description') AS description,
           json_extract(value, '$.category') AS category,
           json_extract(value, '$.created_at') AS created_at,
           json_extract(value, '$.goalId') AS goal_id,
           json_extract(value, '$.opening') AS opening
      FROM json_each(?)
  ),
  ledger AS (
    SELECT t.id, t.date, t.amount, t.description, COALESCE(c.name, '') AS category,
           t.category_id, t.payment_type, t.notes, t.settled, t.created_at,
           0 AS saving, NULL AS goal_id, 0 AS opening
      FROM transactions t
      LEFT JOIN categories c ON c.id = t.category_id
    UNION ALL
    SELECT id, date, amount, description, category, NULL, '', '', 1, created_at,
           1, goal_id, opening
      FROM savings
  ),
  worn (tx, tag) AS (
    SELECT transaction_id, tag_id FROM transaction_tags
    UNION
    SELECT t.id, ct.tag_id
      FROM transactions t
      JOIN category_tags ct ON ct.category_id = t.category_id
  )`

/** The goals' projected rows, as the one parameter `LEDGER` reads them from. */
export const savingsParam = (rows) =>
  JSON.stringify(
    (rows ?? []).map((r) => ({
      id: r.id,
      date: r.date,
      amount: r.amount,
      description: r.description,
      category: r.category,
      created_at: r.created_at,
      goalId: r.goalId,
      opening: r.opening ? 1 : 0,
    })),
  )

/** The columns a listed row carries — the ledger's, plus the tags put on it itself. */
export const ROW_COLUMNS = `l.id, l.date, l.amount, l.description, l.category, l.category_id,
  l.payment_type, l.notes, l.settled, l.created_at, l.saving, l.goal_id, l.opening,
  (SELECT group_concat(tag_id) FROM transaction_tags WHERE transaction_id = l.id) AS own_tags,
  (SELECT COUNT(*) FROM attachments WHERE transaction_id = l.id) AS files`

/**
 * A row as the app reads it: `saving` a boolean, the tags a list, and a saving
 * dressed the way `savingsEntries` dresses one.
 */
export function readRow(r) {
  const saving = !!r.saving
  const row = {
    id: r.id,
    date: r.date,
    amount: r.amount,
    description: r.description ?? '',
    category: r.category ?? '',
    category_id: r.category_id ?? null,
    payment_type: r.payment_type ?? '',
    notes: r.notes ?? '',
    settled: r.settled ?? 1,
    created_at: r.created_at,
    ownTags: r.own_tags ? String(r.own_tags).split(',') : [],
    // How many files are attached to it (docs/attachments-plan.md).
    files: Number(r.files ?? 0),
  }
  if (saving) {
    Object.assign(row, {
      saving: true,
      virtualCategory: true,
      kind: 'savings',
      goalId: r.goal_id,
      opening: !!r.opening,
    })
  }
  return row
}

/** `?, ?, ?` for a list — never empty, so `IN (…)` stays valid SQL. */
export const placeholders = (list) => (list.length ? list.map(() => '?').join(', ') : 'NULL')

/** A LIKE pattern matching `text` anywhere, its own `%` and `_` taken literally. */
export const containing = (text) => `%${String(text).replace(/[\\%_]/g, (c) => `\\${c}`)}%`

/**
 * Everything a search looks through, as one folded string: the words on the
 * row, the tags it wears, and its size written out — so "12.40" finds the
 * coffee. Compare with `fold(?) LIKE` a `containing()` pattern, `ESCAPE '\'`.
 */
export const HAYSTACK = `fold(
  l.description || ' ' || l.category || ' ' || l.payment_type || ' ' || l.notes || ' ' ||
  COALESCE((SELECT group_concat(g.name, ' ') FROM worn w JOIN tags g ON g.id = w.tag WHERE w.tx = l.id), '') ||
  ' ' || printf('%.2f', abs(l.amount))
)`

/** A condition with its parameters; `and`/`or` join them, skipping the empty. */
export const clause = (sql, params = []) => ({ sql, params })

const join = (word) => (parts) => {
  const kept = parts.filter((p) => p && p.sql)
  if (!kept.length) return null
  if (kept.length === 1) return kept[0]
  return clause(`(${kept.map((p) => p.sql).join(` ${word} `)})`, kept.flatMap((p) => p.params))
}
export const and = join('AND')
export const or = join('OR')

/** `WHERE …` for a condition, or nothing. */
export const where = (condition) => (condition ? clause(`WHERE ${condition.sql}`, condition.params) : clause('', []))
